import { useRef, memo } from 'react';
import { Editor as TinyEditor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { AiGeneratedBox } from 'commons/components';
import { AiNavigationAttributes } from 'commons/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { getConfig } from './config';
import './Editor.scss';

interface EditorProps {
  showToolbar: boolean;
  language: string;
  value: string;
  isGeneratedAI?: boolean;
  onChange: (value: string, isUserInput?: boolean) => void;
  navigationAttributes?: AiNavigationAttributes;
}

const BASE_URL = import.meta.env.BASE_URL;
const SCRIPT_SRC = 'js/tinymce/5.10.7/tinymce.min.js';
const TINYMCE_SCRIPT =
  !BASE_URL || BASE_URL === '/' ? SCRIPT_SRC : `${BASE_URL}/${SCRIPT_SRC}`;

function Editor({
  showToolbar,
  language,
  value,
  isGeneratedAI = false,
  onChange,
  navigationAttributes,
}: EditorProps): JSX.Element {
  const t = useTranslationCatalog();
  const editableRef = useRef(false);
  const userInputRef = useRef(false);

  const config = getConfig(t);

  const handleChange = (value: string, _e: TinyMCEEditor) => {
    if (editableRef.current) {
      // Pass whether this change originated from user input
      onChange(value, userInputRef.current);
      // Reset user input flag after handling
      userInputRef.current = false;
    }
  };

  const handleFocus = () => {
    editableRef.current = true;
  };

  const handleFocusOut = () => {
    userInputRef.current = false;
  };

  const handleKeyDown = () => {
    userInputRef.current = true;
  };

  const toolbarEditor = !showToolbar && { toolbar: false, statusbar: false };
  const init = { ...config, language, ...toolbarEditor };

  return (
    <AiGeneratedBox
      aiGenerated={isGeneratedAI}
      aiGeneratedProps={{ id: 'ai-generated-editor-container' }}
      {...navigationAttributes}
    >
      <TinyEditor
        value={value}
        onEditorChange={handleChange}
        tinymceScriptSrc={TINYMCE_SCRIPT}
        init={init}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onFocusOut={handleFocusOut}
      />
    </AiGeneratedBox>
  );
}

export default memo(
  Editor,
  (prev, next) =>
    prev.value === next.value && prev.isGeneratedAI === next.isGeneratedAI,
);
